import { useEffect, useRef, useState } from "react";
import { Environment } from "../../constants/environment.ts";
import { sharedApiHeaders } from "../../constants/sharedApi.ts";
import "./MarketingChat.scss";

const SOURCE = "dispatcher";
const SESSION_KEY = "ertikaz-marketing-chat-session";
const API = `${Environment.ertikazApi}/api/marketing-agent`;

const ARABIC_RE = new RegExp("[\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF]");
const dirFor = (text) => (ARABIC_RE.test(text) ? "rtl" : "auto");

const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderText = (text) =>
  escapeHtml(text)
    .replace(/^#{1,4}\s*(.+)$/gm, "<b>$1</b>")
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/^[-*]\s+/gm, "• ");

const TEXTS = {
  en: {
    title: "Ertikaz Assistant",
    subtitle: "Ask about our products",
    online: "Online",
    placeholder: "Ask anything…",
    send: "Send",
    welcome: "Hi! I'm the Ertikaz assistant. Ask me anything about <b>Lean Dispatcher</b> or Ertikaz Solutions.",
    connectionError: "Connection error — please try again in a moment.",
    tooMany: "You're sending messages too quickly — please wait a minute and try again.",
    ticketTitle: "Your support request has been created.",
    ticketEmail: "Email",
    ticketSubject: "Title",
    ticketBody: "Details",
    ticketFollowup: "A specialist will contact you shortly.",
    openChat: "Chat with our assistant",
    close: "Close",
    tooltip: "Need any help?",
    tooltipHint: "Ask me about Lean Dispatcher",
    suggestions: ["What is Lean Dispatcher?", "Is it approved by TGA and Bayan?", "How can I book a demo?"],
  },
  ar: {
    title: "مساعد ارتكاز",
    subtitle: "اسأل عن منتجاتنا",
    online: "متصل",
    placeholder: "اسأل عن أي شيء…",
    send: "إرسال",
    welcome: "مرحباً! أنا مساعد ارتكاز. يمكنك سؤالي عن <b>لين ديسباتشر</b> أو عن شركة ارتكاز.",
    connectionError: "خطأ في الاتصال — حاول مرة أخرى بعد قليل.",
    tooMany: "أنت ترسل الرسائل بسرعة كبيرة — انتظر دقيقة ثم حاول مجدداً.",
    ticketTitle: "تم إنشاء طلب الدعم الخاص بك.",
    ticketEmail: "البريد",
    ticketSubject: "العنوان",
    ticketBody: "التفاصيل",
    ticketFollowup: "سيتواصل معك أحد المختصين قريباً.",
    openChat: "تحدث مع مساعدنا",
    close: "إغلاق",
    tooltip: "هل تحتاج مساعدة؟",
    tooltipHint: "اسألني عن لين ديسباتشر",
    suggestions: ["ما هو لين ديسباتشر؟", "هل النظام معتمد من هيئة النقل وبيان؟", "كيف أحجز عرضاً توضيحياً؟"],
  },
};

const EYE_BLINK_TIMES = "0;0.18;0.21;0.24;0.62;0.65;0.68;0.71;0.74;1";

const BlinkEye = ({ cx, cy, animated }) => (
  <>
    <ellipse cx={cx} cy={cy} rx="17" ry="17" fill="#42D3AD">
      {animated ? (
        <animate
          attributeName="ry"
          values="17;17;1;17;17;1;17;1;17;17"
          keyTimes={EYE_BLINK_TIMES}
          dur="5.5s"
          repeatCount="indefinite"
        />
      ) : null}
    </ellipse>
    <ellipse cx={cx - 5} cy={cy - 5} rx="5.5" ry="4" fill="#FFFFFF" opacity="0.55">
      {animated ? (
        <>
          <animate
            attributeName="ry"
            values="4;4;0.2;4;4;0.2;4;0.2;4;4"
            keyTimes={EYE_BLINK_TIMES}
            dur="5.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.55;0.55;0;0.55;0.55;0;0.55;0;0.55;0.55"
            keyTimes={EYE_BLINK_TIMES}
            dur="5.5s"
            repeatCount="indefinite"
          />
        </>
      ) : null}
    </ellipse>
  </>
);

const AiPersonIcon = ({ size = 28, animated = true }) => (
  <span
    className={`mkt-chat__ai-person${animated ? "" : " mkt-chat__ai-person--static"}`}
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    <svg
      className="mkt-chat__ai-person-svg"
      viewBox="0 0 677 369"
      width={size}
      height={size}
      fill="none"
    >
      <g>
        {animated ? (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -10; 0 0; 0 -10; 0 0"
            keyTimes="0;0.25;0.5;0.75;1"
            dur="3.2s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1"
          />
        ) : null}
        <g>
          {animated ? (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 338 181; 8 338 181; 0 338 181; -8 338 181; 0 338 181"
              keyTimes="0;0.25;0.5;0.75;1"
              dur="3.2s"
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1"
            />
          ) : null}
          <circle cx="338" cy="180.5" r="76.5" fill="#FFFFFF" stroke="#2B1D42" strokeWidth="7" />
          <ellipse cx="312" cy="146" rx="27" ry="17" fill="#FFFFFF" opacity="0.72" />
          <ellipse cx="352" cy="158" rx="12" ry="8" fill="#EAF4FF" opacity="0.4" />
          <path
            d="M250 142C250 86 298 80 338 80C378 80 426 86 426 142"
            stroke="#2B1D42"
            strokeWidth="24"
            strokeLinecap="round"
          />
          <path
            d="M250 142C250 86 298 80 338 80C378 80 426 86 426 142"
            stroke="#564079"
            strokeWidth="16"
            strokeLinecap="round"
          />
          <rect x="232" y="128" width="34" height="92" rx="17" fill="#73539E" stroke="#2B1D42" strokeWidth="6" />
          <rect x="411" y="128" width="34" height="92" rx="17" fill="#73539E" stroke="#2B1D42" strokeWidth="6" />
          <rect x="280" y="150" width="116" height="78" rx="39" fill="#432F60" stroke="#2B1D42" strokeWidth="5" />
          <BlinkEye cx={313} cy={184} animated={animated} />
          <BlinkEye cx={363} cy={184} animated={animated} />
          <path
            d="M325 203C330 215 346 215 351 203"
            stroke="#42D3AD"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <g className="mkt-chat__ai-mic">
            {animated ? (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 430 205; 7 430 205; 0 430 205; -5 430 205; 0 430 205"
                keyTimes="0;0.28;0.5;0.78;1"
                dur="3.4s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
              />
            ) : null}
            <path
              d="M430 205C426 248 395 268 352 270"
              stroke="#2B1D42"
              strokeWidth="10"
              strokeLinecap="round"
            />
            <rect x="322" y="258" width="42" height="20" rx="10" fill="#42D3AD" stroke="#2B1D42" strokeWidth="4" />
          </g>
        </g>
      </g>
    </svg>
  </span>
);

const AiAvatar = () => (
  <div className="mkt-chat__avatar" aria-hidden="true">
    <AiPersonIcon size={135} animated={false} />
  </div>
);

const LauncherIcon = () => <AiPersonIcon size={120} animated />;

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path d="M5 12h12M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MarketingChat = () => {
  const lang = localStorage.getItem("lang") === "en" ? "en" : "ar";
  const t = TEXTS[lang];

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ kind: "bot", html: t.welcome, dir: lang === "ar" ? "rtl" : "auto" }]);
  const listRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current) {
    let session = sessionStorage.getItem(SESSION_KEY);
    if (!session) {
      session = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^a-z0-9]/gi, "");
      sessionStorage.setItem(SESSION_KEY, session);
    }
    sessionRef.current = session;
  }

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const openChat = () => {
    setOpen(true);
  };

  const send = async (text) => {
    const message = text.trim();
    if (!message || busy) return;

    setMessages((prev) => [...prev, { kind: "user", html: renderText(message), dir: dirFor(message) }]);
    setInput("");
    setBusy(true);
    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: sharedApiHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ sessionId: sessionRef.current, message, source: SOURCE }),
      });
      if (response.status === 429) {
        setMessages((prev) => [...prev, { kind: "bot", html: escapeHtml(t.tooMany), dir: lang === "ar" ? "rtl" : "auto", isError: true }]);
        return;
      }
      if (!response.ok) throw new Error(`http ${response.status}`);
      const reply = await response.json();
      setMessages((prev) => {
        const next = [...prev, { kind: "bot", html: renderText(reply.reply || t.connectionError), dir: dirFor(reply.reply || ""), isError: reply.isError || !reply.reply }];
        if (reply.ticket) next.push({ kind: "ticket", ticket: reply.ticket });
        return next;
      });
    } catch {
      setMessages((prev) => [...prev, { kind: "bot", html: escapeHtml(t.connectionError), dir: lang === "ar" ? "rtl" : "auto", isError: true }]);
    } finally {
      setBusy(false);
    }
  };

  const showSuggestions = messages.length <= 1;
  const canSend = !busy && !!input.trim();
  const showTooltip = !open;

  return (
    <div className={`mkt-chat${open ? " mkt-chat--open" : ""}${lang === "ar" ? " mkt-chat--ar" : " mkt-chat--en"}`}>
      {open && (
        <div className="mkt-chat__panel" dir={lang === "ar" ? "rtl" : "ltr"}>
          <div className="mkt-chat__header">
            <div className="mkt-chat__header-main">
              <AiAvatar />
              <div className="mkt-chat__header-text">
                <div className="mkt-chat__title-row">
                  <div className="mkt-chat__title">{t.title}</div>
                </div>
                <div className="mkt-chat__status">
                  <span className="mkt-chat__online" aria-hidden="true"></span>
                  <span className="mkt-chat__online-label">{t.online}</span>
                </div>
                <div className="mkt-chat__subtitle">{t.subtitle}</div>
              </div>
            </div>
            <button type="button" className="mkt-chat__close" onClick={() => setOpen(false)} aria-label={t.close}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="mkt-chat__messages" ref={listRef}>
            {messages.map((m, i) =>
              m.kind === "ticket" ? (
                <div key={i} className="mkt-chat__ticket" dir={dirFor(m.ticket.title + " " + m.ticket.body)}>
                  <div className="mkt-chat__ticket-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#42D3AD" opacity="0.2" />
                      <path d="M7.5 12.5l3 3 6-6.5" stroke="#42D3AD" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="mkt-chat__ticket-body">
                    <h4>{t.ticketTitle}</h4>
                    <div className="mkt-chat__ticket-number">Ticket #{m.ticket.ticketNumber}</div>
                    <div><span>{t.ticketEmail}:</span> {m.ticket.email}</div>
                    <div><span>{t.ticketSubject}:</span> {m.ticket.title}</div>
                    <div><span>{t.ticketBody}:</span> {m.ticket.body}</div>
                    <p className="mkt-chat__ticket-followup">{t.ticketFollowup}</p>
                  </div>
                </div>
              ) : (
                <div key={i} className={`mkt-chat__msg mkt-chat__msg--${m.kind}${m.isError ? " mkt-chat__msg--error" : ""}`}>
                  {m.isError && (
                    <span className="mkt-chat__error-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                        <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
                        <path d="M12 3.5L2.8 19h18.4L12 3.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none" />
                      </svg>
                    </span>
                  )}
                  <div className="mkt-chat__bubble" dir={m.dir} dangerouslySetInnerHTML={{ __html: m.html }} />
                </div>
              )
            )}
            {busy && (
              <div className="mkt-chat__msg mkt-chat__msg--bot mkt-chat__msg--typing">
                <div className="mkt-chat__bubble mkt-chat__bubble--typing" aria-label="Assistant is typing">
                  <span className="mkt-chat__dot mkt-chat__dot--1" />
                  <span className="mkt-chat__dot mkt-chat__dot--2" />
                  <span className="mkt-chat__dot mkt-chat__dot--3" />
                </div>
              </div>
            )}
          </div>

          {showSuggestions && (
            <div className="mkt-chat__suggestions">
              {t.suggestions.map((s, index) => (
                <button
                  key={s}
                  type="button"
                  style={{ "--chip-delay": `${index * 80}ms` }}
                  onClick={() => send(s)}
                  disabled={busy}
                >
                  <span className="mkt-chat__chip-icon" aria-hidden="true">✦</span>
                  <span>{s}</span>
                </button>
              ))}
            </div>
          )}

          <form
            className="mkt-chat__composer"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <input
              type="text"
              value={input}
              dir="auto"
              placeholder={t.placeholder}
              onChange={(e) => setInput(e.target.value)}
            />
            <button type="submit" disabled={!canSend} aria-label={t.send}>
              <SendIcon />
            </button>
          </form>
        </div>
      )}

      <div className="mkt-chat__dock">
        {showTooltip && (
          <div
            className={`mkt-chat__tooltip${lang === "ar" ? " mkt-chat__tooltip--rtl" : ""}`}
            dir={lang === "ar" ? "rtl" : "ltr"}
            role="status"
          >
            <button type="button" className="mkt-chat__tooltip-body" onClick={openChat}>
              <span className="mkt-chat__tooltip-title">{t.tooltip}</span>
              <span className="mkt-chat__tooltip-hint">{t.tooltipHint}</span>
            </button>
            <span className="mkt-chat__tooltip-tail" aria-hidden="true" />
          </div>
        )}

        <button
          type="button"
          className={`mkt-chat__launcher${open ? " mkt-chat__launcher--open" : ""}`}
          onClick={() => (open ? setOpen(false) : openChat())}
          aria-label={open ? t.close : t.openChat}
          title={t.openChat}
        >
          <LauncherIcon />
        </button>
      </div>
    </div>
  );
};

export default MarketingChat;
